import React, {useCallback, useState} from 'react';
import {Modal, Form, Input, Button} from 'antd';
import {tid} from '../../../../../../testUtils';
import {useMounted} from '../../../../../../hooks/useMounted';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {useDispatch} from 'react-redux';
import {requestNotificationCreation} from '../../../../../../store/notification/api';

export type NotificationConfirmationDialogAntdProps = {
  title: string;
  text: string;
  description: string;
  label: string;
  open: boolean;
  successMessage: string;
  type: string;
  uuid?: string;
  handleCancel: () => void;
};

const NotificationConfirmationDialogAntd: React.FC<NotificationConfirmationDialogAntdProps> = ({
  title,
  text,
  description,
  label,
  open,
  successMessage,
  type,
  uuid,
  handleCancel,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const mounted = useMounted();
  const [form] = Form.useForm<{userNote: string}>();

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!uuid) return;
      setSubmitting(true);
      await requestNotificationCreation(uuid, type, values.userNote);
      dispatch(messageAdd(successMessage, MessageTypes.SUCCESS));
      form.resetFields();
      handleCancel();
    } catch (error: any) {
      if (error?.errorFields) {
        // Ant Design validation failure — form already shows inline errors, do nothing
        return;
      }
      if (mounted.current) {
        dispatch(messageAdd('Failed to send email notification', MessageTypes.ERROR));
      }
    } finally {
      if (mounted.current) {
        setSubmitting(false);
      }
    }
  }, [form, uuid, type, successMessage, dispatch, handleCancel, mounted]);

  const handleNo = useCallback(() => {
    form.resetFields();
    handleCancel();
  }, [form, handleCancel]);

  return (
    <Modal
      open={open}
      title={title}
      closable={false}
      maskClosable={false}
      footer={
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
          <Button onClick={handleNo} {...tid('dialog-notify-cancel', 'cancel')}>
            No
          </Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit} {...tid('dialog-notify-submit', 'submit')}>
            Yes
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <p style={{marginBottom: '8px'}}>{text}</p>
        <p style={{marginBottom: '16px', color: '#595959'}}>{description}</p>
        <Form.Item
          name="userNote"
          required={false}
          rules={[{required: true, message: `Please provide users more details regarding ${label}.`}]}
          label={
            <span style={{color: '#595959'}}>
              Please provide users more details regarding {label}.<span style={{color: '#ff4d4f'}}> *</span>
            </span>
          }
          colon={false}
        >
          <Input.TextArea rows={4} {...tid('input', 'message')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NotificationConfirmationDialogAntd;
